import { BlockRenderer, BlockList, type Block } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

const markdownBlock: Block = {
  block_id: "blk_md",
  type: "markdown",
  props: {
    text: "### Synthesis\nThe collective-intelligence run found **3 themes**. Each renders inline:\n\n- markdown like this\n- a `code` block\n- a `stat` card",
  },
};

const statBlock: Block = {
  block_id: "blk_stat",
  type: "stat",
  props: { label: "Confidence", value: 92, unit: "%", delta: 4 },
};

const codeBlock: Block = {
  block_id: "blk_code",
  type: "code",
  props: {
    language: "python",
    source: "def synthesize(sources):\n    return rank(sources, key=support)",
  },
};

const tableBlock: Block = {
  block_id: "blk_table",
  type: "table",
  props: {
    columns: ["Source", "Support"],
    rows: [
      ["arxiv.org", "0.91"],
      ["nature.com", "0.84"],
      ["wikipedia.org", "0.62"],
    ],
    sort: { by: "Support", direction: "desc" },
  },
};

const citationBlock: Block = {
  block_id: "blk_cite",
  type: "citation",
  props: {
    title: "K2 block schema registry",
    url: "https://docs.aisquare.studio/contracts/block-registry",
    snippet: "Blocks are how the LLM puts rich UI in chat.",
  },
};

// A `chart` block EXACTLY as the runtime emits / the BE normalizes it - the
// flat BE#2577 contract at the props top level, no `spec` wrapper. This is
// what a chat host renders with zero registry overrides.
const chartBlock: Block = {
  block_id: "blk_chart",
  type: "chart",
  version: 1,
  props: {
    chart_type: "bar",
    orientation: "vertical",
    stacked: false,
    title: "Support by theme",
    x: { key: "theme", label: "Theme", type: "category" },
    series: [{ key: "support", label: "Support", color: null }],
    data: [
      { theme: "Retrieval quality", support: 0.91 },
      { theme: "Latency", support: 0.84 },
      { theme: "Cost", support: 0.62 },
    ],
    value_format: "float",
    summary: "Retrieval quality has the strongest support at 0.91.",
  },
};

const htmlBlock: Block = {
  block_id: "blk_html",
  type: "html",
  props: {
    srcdoc:
      '<style>body{font-family:system-ui;margin:0;padding:16px}h4{margin:0 0 8px}</style><h4>Sandboxed HTML</h4><p>Rendered in an iframe with <code>sandbox="allow-scripts"</code> and a strict CSP. No same-origin access.</p>',
  },
};

const unknownBlock: Block = {
  block_id: "blk_unknown",
  type: "quantum-widget",
  props: { whatever: true },
};

const malformedBlock: Block = {
  block_id: "blk_bad",
  type: "stat",
  props: { label: "missing value" },
};

export function BlockRendererDemo() {
  return (
    <Section
      id="block-renderer"
      title="BlockRenderer"
      description="The single render seam both FE chats consume. Takes a K2 block envelope ({ block_id, type, version, props }) and renders the matching widget; markdown/text -> Markdown, html -> a sandboxed iframe, unknown / malformed -> a safe placeholder. An extensible registry map lets consumers add or override renderers."
    >
      <VariantBlock label="A streamed run, rendered as a block list">
        <div className="w-full max-w-xl">
          <BlockList
            blocks={[markdownBlock, statBlock, chartBlock, tableBlock, codeBlock, citationBlock]}
          />
        </div>
      </VariantBlock>

      <VariantBlock label="html block" caption="sandboxed iframe, no same-origin">
        <div className="w-full max-w-xl">
          <BlockRenderer block={htmlBlock} />
        </div>
      </VariantBlock>

      <VariantBlock label="Safe fallbacks" caption="unknown type + malformed props never crash">
        <div className="w-full max-w-xl space-y-3">
          <BlockRenderer block={unknownBlock} />
          <BlockRenderer block={malformedBlock} />
        </div>
      </VariantBlock>

      <VariantBlock label="Registry override" caption="consumer-supplied renderer for a new type">
        <div className="w-full max-w-xl">
          <BlockRenderer
            block={{ block_id: "blk_form", type: "form", props: { title: "Quiz" } }}
            registry={{
              form: ({ block }) => (
                <div className="rounded-xl border border-accent/30 bg-accent/5 p-3 text-sm text-foreground">
                  Custom <span className="font-medium">{block.type}</span> renderer
                  (consumer-supplied)
                </div>
              ),
            }}
          />
        </div>
      </VariantBlock>
    </Section>
  );
}
